/**
 * Helpers for shuttling data between the SQL `/execute-sql` response
 * shape (`{columns: string[], rows: any[][]}`) and the columnar shape
 * react-chart-editor + react-plotly.js expects (`{col: any[]}`).
 *
 * Plotly's "src" pattern lets a saved chart reference columns by name
 * (e.g. trace ``{xsrc: "owner_name", ysrc: "ticket_count"}``) so that
 * when fresh data comes in we re-hydrate the actual ``x`` / ``y``
 * arrays without storing the values inside the spec itself.
 */

/**
 * Convert SQL row results into a columnar dict the editor can consume.
 * @param {string[]} columns
 * @param {any[][]} rows
 * @returns {Record<string, any[]>}
 */
export function rowsToColumnar(columns, rows) {
  const out = {};
  for (const col of columns) out[col] = [];
  for (const row of rows) {
    for (let i = 0; i < columns.length; i += 1) {
      out[columns[i]].push(row[i]);
    }
  }
  return out;
}

/**
 * Build the ``dataSources`` + ``dataSourceOptions`` props needed by
 * ``react-chart-editor``.
 */
export function buildEditorDataSources(columns, rows) {
  const dataSources = rowsToColumnar(columns, rows);
  const dataSourceOptions = columns.map((c) => ({ value: c, label: c }));
  return { dataSources, dataSourceOptions };
}

/**
 * Replace ``*src`` string references on each trace of ``figure.data``
 * with the actual column array from ``dataSources``. Returns a new
 * figure — does not mutate the spec.
 */
const SRC_ATTRS = [
  'x', 'y', 'z',
  'labels', 'values',
  'text', 'hovertext',
  'open', 'high', 'low', 'close',
  'r', 'theta', 'a', 'b', 'c',
  'lat', 'lon', 'locations',
  'header.values', 'cells.values',
];

function getPath(obj, path) {
  return path.split('.').reduce((o, k) => (o == null ? undefined : o[k]), obj);
}
function setPath(obj, path, value) {
  const parts = path.split('.');
  let cur = obj;
  for (let i = 0; i < parts.length - 1; i += 1) {
    const k = parts[i];
    if (cur[k] == null || typeof cur[k] !== 'object') cur[k] = {};
    cur = cur[k];
  }
  cur[parts[parts.length - 1]] = value;
}

function hydrateTrace(trace, dataSources) {
  const out = { ...trace };
  for (const attr of SRC_ATTRS) {
    const srcKey = `${attr.split('.').pop()}src`;
    const srcPath = attr.includes('.')
      ? `${attr.split('.').slice(0, -1).join('.')}.${srcKey}`
      : srcKey;
    const srcVal = getPath(trace, srcPath);
    if (typeof srcVal === 'string' && dataSources[srcVal]) {
      setPath(out, attr, dataSources[srcVal]);
    }
  }
  if (trace.marker) {
    const m = { ...trace.marker };
    if (typeof m.sizesrc === 'string' && dataSources[m.sizesrc]) m.size = dataSources[m.sizesrc];
    if (typeof m.colorsrc === 'string' && dataSources[m.colorsrc]) m.color = dataSources[m.colorsrc];
    // ``colorMap`` (also tolerates lowercase ``colormap``) is our
    // declarative "color this category that color" primitive. It
    // resolves to a per-row colors array at render time so the
    // saved spec stays robust to row reordering / partial results.
    //
    //   marker.colorMap  : Record<string, string>   (value → color)
    //   marker.colorssrc : pie   per-slice colors    → marker.colors
    //   marker.colorsrc  : other per-point colors    → marker.color
    //   labelssrc        : pie default category col (when no *src given)
    //   xsrc             : bar/scatter default category col
    const colorMap = (m.colorMap && typeof m.colorMap === 'object')
      ? m.colorMap
      : ((m.colormap && typeof m.colormap === 'object') ? m.colormap : null);
    if (colorMap) {
      const fallback = (trace.type === 'pie') ? trace.labelssrc : trace.xsrc;
      // Pie wants ``marker.colors`` (array), every other trace uses
      // ``marker.color`` (which Plotly happily accepts as an array).
      const isPie = trace.type === 'pie';
      const categorySrc = isPie
        ? (typeof m.colorssrc === 'string' ? m.colorssrc : fallback)
        : (typeof m.colorsrc === 'string' ? m.colorsrc : fallback);
      const categoryArr = (typeof categorySrc === 'string') ? dataSources[categorySrc] : null;
      if (Array.isArray(categoryArr)) {
        const colors = categoryArr.map((v) => colorMap[v] ?? colorMap[String(v)] ?? undefined);
        if (isPie) m.colors = colors;
        else m.color = colors;
      }
      // Strip the template-only key so it never reaches Plotly.
      delete m.colorMap;
      delete m.colormap;
    }
    out.marker = m;
  }
  return out;
}

/**
 * Filter every column array in ``dataSources`` down to the rows whose
 * value at ``splitColumn`` equals ``value``. Returns a new dataSources
 * dict with the same column keys but shorter arrays. Used by
 * ``hydrateFigure`` to expand a single ``splitBy`` template trace into
 * one trace per distinct pivot value.
 */
function filterDataSources(dataSources, splitColumn, value) {
  const col = dataSources?.[splitColumn];
  if (!Array.isArray(col)) return dataSources;
  const keep = [];
  for (let i = 0; i < col.length; i += 1) if (col[i] === value) keep.push(i);
  const out = {};
  for (const k of Object.keys(dataSources)) {
    const src = dataSources[k];
    out[k] = Array.isArray(src) ? keep.map((i) => src[i]) : src;
  }
  return out;
}

/**
 * Expand a ``splitBy`` template trace into one hydrated trace per
 * distinct value of the pivot column. The template must reference its
 * data columns via ``*src`` keys; this function then filters the row
 * set per value and hydrates each subset into its own trace.
 *
 * Schema on the template trace:
 *   splitBy:     string  — pivot column name (must exist in dataSources)
 *   splitOrder:  string[] (optional) — fixed value order; otherwise
 *                first-seen order from the column data
 *   splitColors: Record<string,string> (optional) — per-value color
 *                applied as ``marker.color`` on the expanded trace
 *
 * Returns ``null`` when the template is not actually a splitBy trace,
 * so the caller can fall back to single-trace hydration.
 */
function expandSplitTrace(trace, dataSources) {
  const splitBy = trace?.splitBy;
  if (typeof splitBy !== 'string' || !splitBy) return null;
  const col = dataSources?.[splitBy];
  if (!Array.isArray(col)) return null;
  const seen = [];
  const seenSet = new Set();
  for (const v of col) {
    if (seenSet.has(v)) continue;
    seenSet.add(v);
    seen.push(v);
  }
  const order = Array.isArray(trace.splitOrder) && trace.splitOrder.length
    ? trace.splitOrder.filter((v) => seenSet.has(v)).concat(seen.filter((v) => !trace.splitOrder.includes(v)))
    : seen;
  const colors = (trace.splitColors && typeof trace.splitColors === 'object') ? trace.splitColors : null;
  // Strip the template-only keys before per-value hydration so they
  // don't leak into the Plotly trace passed to react-plotly.
  const template = { ...trace };
  delete template.splitBy;
  delete template.splitOrder;
  delete template.splitColors;
  return order.map((value) => {
    const subset = filterDataSources(dataSources, splitBy, value);
    const hydrated = hydrateTrace(template, subset);
    hydrated.name = String(value);
    if (colors && typeof colors[value] === 'string') {
      hydrated.marker = { ...(hydrated.marker || {}), color: colors[value] };
    }
    return hydrated;
  });
}

/**
 * Hydrate a saved figure with fresh column data.
 *
 * Trace forms supported:
 *   - Standard trace with ``*src`` column references → hydrated in place.
 *   - Template trace carrying a ``splitBy`` column → expanded into one
 *     trace per distinct pivot value (replaces snapshot multi-trace
 *     pivots so charts stay live).
 *
 * @param {{data: any[], layout?: any, frames?: any[]}} figure
 * @param {Record<string, any[]>} dataSources
 */
export function hydrateFigure(figure, dataSources) {
  const data = [];
  for (const t of (figure?.data || [])) {
    const split = expandSplitTrace(t, dataSources);
    if (split) {
      data.push(...split);
    } else {
      data.push(hydrateTrace(t, dataSources));
    }
  }
  return {
    data,
    layout: figure?.layout || {},
    frames: figure?.frames || [],
  };
}

/**
 * Normalize a figure spec produced by the AI assistant so that it
 * survives both the chart-editor UI (which reads `*src` to populate
 * its column pickers) and Plotly itself (which only draws inline
 * arrays).
 */
export function normalizeFigure(figure, dataSources) {
  if (!figure || typeof figure !== 'object') return figure;
  const cols = Object.keys(dataSources || {});
  const arrayToCol = {};
  for (const col of cols) {
    try { arrayToCol[JSON.stringify(dataSources[col])] = col; } catch { /* ignore */ }
  }
  const data = (figure.data || []).map((trace) => {
    let t = { ...trace };
    if (t.type === 'line') {
      t.type = 'scatter';
      t.mode = t.mode || 'lines';
    } else if (t.type === 'area') {
      t.type = 'scatter';
      t.mode = t.mode || 'lines';
      t.fill = t.fill || 'tozeroy';
    }
    for (const attr of SRC_ATTRS) {
      const last = attr.split('.').pop();
      const srcPath = attr.includes('.')
        ? `${attr.split('.').slice(0, -1).join('.')}.${last}src`
        : `${last}src`;
      const existingSrc = getPath(t, srcPath);
      if (typeof existingSrc === 'string' && dataSources[existingSrc]) continue;
      const inline = getPath(t, attr);
      if (typeof inline === 'string' && dataSources[inline]) {
        setPath(t, srcPath, inline);
        setPath(t, attr, dataSources[inline]);
        continue;
      }
      if (Array.isArray(inline)) {
        try {
          const col = arrayToCol[JSON.stringify(inline)];
          if (col) setPath(t, srcPath, col);
        } catch { /* ignore */ }
      }
    }
    return t;
  });
  return { ...figure, data };
}
